import {
  type Quiz,
  type QuizAttempt,
  type QuizDraftQuestion,
  type ViewContext,
  addDays,
  isQuizOpen,
  sameAnswer,
  shortDate,
  todayIso,
  useClasses,
  useCourseSize,
  useCourses,
  useCurrentContext,
  useQuizActions,
  useQuizAttempts,
  useQuizRetakes,
  awayTime,
  useQuizKeys,
  useQuizQuestions,
  useQuizReview,
  useQuizzes,
} from '@shkola/api';
import { subjectLook } from '@shkola/design';
import { ArrowRightIcon } from '@phosphor-icons/react/ArrowRight';
import { EyeIcon } from '@phosphor-icons/react/Eye';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SpeakButton } from '../components/SpeakButton';
import { Button, ErrorText, Field, Modal, Spinner } from '../components/ui';
import {
  Block,
  GRAD,
  type Grad,
  Head,
  INK,
  INK_FAINT,
  INK_SOFT,
  LINE,
  PAPER,
  PageHead,
  PillIcon,
  SUBJECT_GRAD,
  SubjectArt,
  TaskArt,
  grad,
  pillClass,
  ringSmall,
} from '../design/forms';
import {
  ArrowCounterClockwiseIcon,
  ArrowUpRightIcon,
  CaretDownIcon,
  CheckIcon,
  PlusIcon,
  WarningCircleIcon,
  XIcon,
} from '../design/icons';

/*
 * «Тести» in «Форми 2» (28.10, the spread of the look over every section). The student: one block —
 * what is to take on top as soft cards in the subject's colour with its picture big and quiet behind and
 * a dark pill to start, then the results as a ruled list (the subject's small picture, the score as a soft
 * mark in its level's colour, the answers opening under the row). The test itself in the dialog: the
 * options as soft rows, the chosen one lavender. The teacher: the course chosen in the title's row, the
 * tests as rows that open the results — the students, how each question went.
 */

/**
 * The page's title with its controls: beside the title on a wide screen, in a row under it on a phone
 * (beside the big title they would squeeze it).
 */
function TitleRow({ children, ...head }: Parameters<typeof PageHead>[0]) {
  return (
    <div className="flex flex-col gap-3">
      <PageHead {...head}>
        {children && (
          <span className="hidden flex-wrap items-center gap-2 lg:flex">{children}</span>
        )}
      </PageHead>
      {children && <div className="flex flex-wrap items-center gap-2 lg:hidden">{children}</div>}
    </div>
  );
}

/** the colour of a share as of a grade: low coral, then butter, sky, lime */
const LEVEL: ReadonlyArray<Grad> = ['coral', 'butter', 'sky', 'lime'];
const toneOfShare = (score: number, max: number): Grad => {
  const points = Math.max(1, Math.round((12 * score) / Math.max(1, max)));
  return LEVEL[Math.min(3, Math.floor((points - 1) / 3))] ?? 'stone';
};

/** A label: a soft rectangle in its colour (a pill is a button). */
function Mark({ tone, children }: { tone: Grad; children: ReactNode }) {
  return (
    <span
      className="inline-flex h-6 shrink-0 items-center gap-1 rounded-[7px] px-2 text-[12px] leading-none font-medium whitespace-nowrap"
      style={{ backgroundImage: grad(tone), color: GRAD[tone].ink }}
    >
      {children}
    </span>
  );
}

/** A score: a small soft square of its level, the words light. */
function Score({ attempt }: { attempt: Pick<QuizAttempt, 'score' | 'maxScore'> }) {
  const { t } = useTranslation();
  const tone = toneOfShare(attempt.score, attempt.maxScore);
  return (
    <span
      className="inline-flex h-9 shrink-0 items-center rounded-[10px] px-2.5 font-display text-[17px] font-light tracking-[-0.01em] whitespace-nowrap"
      style={{ backgroundImage: grad(tone), color: INK }}
    >
      {t('quizzes.score', { score: attempt.score, max: attempt.maxScore })}
    </span>
  );
}

const selectClass =
  'h-10 max-w-full rounded-full bg-white px-4 text-[14px] font-medium text-[var(--sh-ink)] ring-1 ring-[#E4E0D7] outline-none ring-inset focus:ring-2 focus:ring-[var(--sh-dark)]';

function Review({ attemptId }: { attemptId: string }) {
  const { t } = useTranslation();
  const review = useQuizReview(attemptId);
  if (review.isPending) return <Spinner />;
  if (review.isError)
    return (
      <p className="text-[14px]" style={{ color: INK_SOFT }}>
        {t('quizzes.noReview')}
      </p>
    );
  return (
    <ol className="flex max-w-3xl flex-col gap-5">
      {(review.data ?? []).map((q) => (
        <li key={q.questionId}>
          <p className="text-[15px] leading-6 font-medium" style={{ color: INK }}>
            <span style={{ color: INK_FAINT }}>{q.position}.</span> {q.question}
          </p>
          <ul className="mt-2 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
            {q.options.map((o, i) => {
              const right = q.correct.includes(i);
              const chosen = q.chosen.includes(i);
              const tone: Grad | undefined = right ? 'mint' : chosen ? 'coral' : undefined;
              return (
                <li
                  // options of a question never change order
                  key={o}
                  className="flex items-center gap-2.5 rounded-[14px] px-3 py-2 text-[14px] leading-5"
                  style={
                    tone
                      ? { backgroundImage: grad(tone), color: INK }
                      : { backgroundColor: PAPER, color: INK_SOFT }
                  }
                >
                  <span
                    className="flex size-5 shrink-0 items-center justify-center rounded-full"
                    style={{
                      backgroundColor: tone ? 'rgb(255 255 255 / 0.85)' : '#FFFFFF',
                      color: tone ? GRAD[tone].ink : INK_FAINT,
                    }}
                  >
                    {right ? (
                      <CheckIcon size={11} weight="bold" aria-hidden />
                    ) : chosen ? (
                      <XIcon size={11} weight="bold" aria-hidden />
                    ) : null}
                  </span>
                  {o}
                </li>
              );
            })}
          </ul>
        </li>
      ))}
    </ol>
  );
}

/**
 * «Чесний тест»: how often and how long the page was left (another tab, another window) while the
 * test was open. Nothing is blocked; the teacher sees it next to the result.
 */
function useAway() {
  const state = useRef({ count: 0, seconds: 0, since: null as number | null });
  useEffect(() => {
    const away = () => {
      if (state.current.since !== null) return;
      state.current.since = Date.now();
      state.current.count += 1;
    };
    const back = () => {
      if (state.current.since === null) return;
      state.current.seconds += Math.round((Date.now() - state.current.since) / 1000);
      state.current.since = null;
    };
    const visibility = () => (document.hidden ? away() : back());
    document.addEventListener('visibilitychange', visibility);
    window.addEventListener('blur', away);
    window.addEventListener('focus', back);
    return () => {
      document.removeEventListener('visibilitychange', visibility);
      window.removeEventListener('blur', away);
      window.removeEventListener('focus', back);
    };
  }, []);
  return () => ({ count: state.current.count, seconds: state.current.seconds });
}

function TakeQuiz({ quiz, onClose }: { quiz: Quiz; onClose: () => void }) {
  const { t } = useTranslation();
  const away = useAway();
  const questions = useQuizQuestions(quiz.id);
  const actions = useQuizActions();
  const [answers, setAnswers] = useState<Record<string, number[]>>({});
  const [clientId] = useState(() => crypto.randomUUID());
  const [result, setResult] = useState<{ score: number; max_score: number }>();
  const [error, setError] = useState<string>();
  const unanswered = (questions.data ?? []).filter((q) => !(answers[q.id] ?? []).length).length;

  return (
    <Modal title={quiz.title} onClose={onClose}>
      {result ? (
        <div className="flex flex-col items-center py-4 text-center">
          <TaskArt kind="done" tone={toneOfShare(result.score, result.max_score)} size={96} />
          <p
            className="mt-3 font-display text-[26px] leading-8 font-light tracking-[-0.02em]"
            style={{ color: INK }}
          >
            {t('quizzes.yourScore', { score: result.score, max: result.max_score })}
          </p>
          <button type="button" onClick={onClose} className={`${pillClass('light')} mt-5 pr-4.5`}>
            {t('common.close')}
          </button>
        </div>
      ) : questions.isPending ? (
        <Spinner />
      ) : (
        <form
          className="flex flex-col gap-6"
          onSubmit={(e) => {
            e.preventDefault();
            setError(undefined);
            actions.submit
              .mutateAsync({ quizId: quiz.id, answers, clientId, away: away() })
              .then(setResult, (err: Error) =>
                setError(
                  err.message.includes('closed')
                    ? t('quizzes.errors.closed')
                    : err.message.includes('already')
                      ? t('quizzes.errors.already')
                      : t('common.error'),
                ),
              );
          }}
        >
          {quiz.description && (
            <p className="-mt-2 text-[14px] leading-[21px]" style={{ color: INK_SOFT }}>
              {quiz.description}
            </p>
          )}
          {(questions.data ?? []).map((q) => (
            <fieldset key={q.id} className="flex flex-col gap-2">
              <legend className="mb-2 flex w-full flex-col gap-2">
                <span className="text-[16px] leading-6 font-medium" style={{ color: INK }}>
                  <span style={{ color: INK_FAINT }}>{q.position}.</span> {q.text}
                </span>
                <span className="flex flex-wrap items-center gap-2">
                  {q.multiple && <Mark tone="lavender">{t('quizzes.multipleHint')}</Mark>}
                  <SpeakButton
                    text={`${q.text}. ${q.options.join(', ')}`}
                    className="!px-2 !py-0.5 text-xs"
                  />
                </span>
              </legend>
              {q.options.map((o, i) => {
                const chosen = (answers[q.id] ?? []).includes(i);
                return (
                  <label
                    key={o}
                    className="flex cursor-pointer items-center gap-3 rounded-[16px] px-3.5 py-3 text-[15px] leading-[21px] transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[var(--sh-dark)]"
                    style={
                      chosen
                        ? { backgroundImage: grad('lavender'), color: INK }
                        : { backgroundColor: PAPER, color: INK }
                    }
                  >
                    <input
                      type={q.multiple ? 'checkbox' : 'radio'}
                      name={q.id}
                      checked={chosen}
                      className="sr-only"
                      onChange={(e) =>
                        setAnswers({
                          ...answers,
                          [q.id]: q.multiple
                            ? e.target.checked
                              ? [...(answers[q.id] ?? []), i]
                              : (answers[q.id] ?? []).filter((x) => x !== i)
                            : [i],
                        })
                      }
                    />
                    {/* the mark of the choice: a round one for one answer, a soft square for several */}
                    <span
                      aria-hidden
                      className={`flex size-5 shrink-0 items-center justify-center text-white ${q.multiple ? 'rounded-[6px]' : 'rounded-full'}`}
                      style={
                        chosen
                          ? { backgroundColor: 'var(--sh-dark)' }
                          : { backgroundColor: '#FFFFFF', boxShadow: `inset 0 0 0 1.5px ${LINE}` }
                      }
                    >
                      {chosen && <CheckIcon size={11} weight="bold" />}
                    </span>
                    {o}
                  </label>
                );
              })}
            </fieldset>
          ))}
          <div
            className="flex flex-col gap-1.5 rounded-[16px] p-3.5 text-[13px] leading-5"
            style={{ backgroundColor: PAPER, color: INK_SOFT }}
          >
            <p>
              {t('quizzes.once')}
              {unanswered > 0 && (
                <span style={{ color: GRAD.coral.ink }}>
                  {' '}
                  {t('quizzes.unanswered', { count: unanswered })}
                </span>
              )}
            </p>
            <p className="flex gap-2">
              <EyeIcon size={16} weight="regular" aria-hidden className="mt-0.5 shrink-0" />
              {t('quizzes.fairHint')}
            </p>
          </div>
          <ErrorText>{error}</ErrorText>
          <Button type="submit" disabled={actions.submit.isPending}>
            {actions.submit.isPending ? t('quizzes.sending') : t('quizzes.send')}
          </Button>
        </form>
      )}
    </Modal>
  );
}

/** A test to take: a soft card in its subject's colour, the picture big and quiet behind. */
function ToTake({
  quiz,
  attempt,
  self,
  onTake,
}: {
  quiz: Quiz;
  attempt: QuizAttempt | undefined;
  self: boolean;
  onTake: () => void;
}) {
  const { t } = useTranslation();
  const look = subjectLook(quiz.subject);
  const tone = SUBJECT_GRAD[look.tone];
  return (
    <article
      className="relative isolate flex min-h-[176px] min-w-0 flex-col overflow-hidden rounded-[24px] p-4 md:p-5"
      style={{ backgroundImage: grad(tone), color: GRAD[tone].ink }}
    >
      <SubjectArt
        icon={look.icon}
        tone={tone}
        size={132}
        ghost
        className="pointer-events-none absolute -top-5 -right-5 -z-10"
      />
      <p className="text-[13px] font-medium">{quiz.subject}</p>
      <p
        className="mt-1 max-w-[85%] font-display text-[22px] leading-[28px] font-light tracking-[-0.02em]"
        style={{ color: INK }}
      >
        {quiz.title}
      </p>
      <p className="mt-1 text-[13px]" style={{ color: INK_SOFT }}>
        {t('quizzes.questions', { count: quiz.questions })} ·{' '}
        {t('quizzes.due', { date: shortDate(quiz.dueOn) })}
      </p>
      <div className="mt-auto flex flex-wrap items-center gap-2 pt-4">
        {self ? (
          <button type="button" onClick={onTake} className={pillClass('dark')}>
            {attempt ? t('quizzes.retakeTake') : t('quizzes.take')}
            <PillIcon icon={ArrowRightIcon} />
          </button>
        ) : (
          <span
            className="rounded-[7px] bg-white/80 px-2 py-1 text-[12px] leading-none font-medium"
            style={{ color: INK }}
          >
            {attempt ? t('quizzes.retakeParent') : t('quizzes.notTakenParent')}
          </span>
        )}
        {attempt && <Score attempt={attempt} />}
      </div>
    </article>
  );
}

/** A test done or closed: a ruled row, the score as a soft mark, the answers opening under it. */
function ResultRow({ quiz, attempt }: { quiz: Quiz; attempt: QuizAttempt | undefined }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const look = subjectLook(quiz.subject);
  const canReview = !!attempt && quiz.showAnswers;
  return (
    <li style={{ borderTop: `1px solid ${LINE}` }}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 py-3">
        <SubjectArt icon={look.icon} tone={SUBJECT_GRAD[look.tone]} size={40} />
        <span className="min-w-0 flex-1">
          <span className="block text-[16px] leading-[22px] font-medium" style={{ color: INK }}>
            {quiz.title}
          </span>
          <span className="block text-[12.5px] leading-[18px]" style={{ color: INK_SOFT }}>
            {quiz.subject} · {t('quizzes.questions', { count: quiz.questions })} ·{' '}
            {isQuizOpen(quiz, todayIso())
              ? t('quizzes.due', { date: shortDate(quiz.dueOn) })
              : t('quizzes.closed')}
          </span>
        </span>
        <span className="flex basis-full flex-wrap items-center gap-2 pl-14 sm:basis-auto sm:pl-0">
          {attempt ? (
            <>
              {attempt.attemptNo > 1 && (
                <span className="text-[12.5px]" style={{ color: INK_SOFT }}>
                  {t('quizzes.attemptNo', { n: attempt.attemptNo })}
                </span>
              )}
              <Score attempt={attempt} />
            </>
          ) : (
            <Mark tone="stone">{t('quizzes.missed')}</Mark>
          )}
          {canReview && (
            <button
              type="button"
              aria-expanded={open}
              onClick={() => setOpen(!open)}
              className={`${pillClass('light', 'sm')} pr-3.5`}
            >
              {open ? t('quizzes.hideReview') : t('quizzes.review')}
              <CaretDownIcon
                size={12}
                weight="bold"
                aria-hidden
                className={`transition-transform duration-300 ease-spring ${open ? 'rotate-180' : ''}`}
              />
            </button>
          )}
        </span>
      </div>
      {attempt && open && (
        <div className="motion-rise pb-5 sm:pl-14">
          <Review attemptId={attempt.id} />
        </div>
      )}
    </li>
  );
}

function FamilyView({ ctx }: { ctx: Extract<ViewContext, { kind: 'student' | 'guardian' }> }) {
  const { t } = useTranslation();
  const today = todayIso();
  const quizzes = useQuizzes({ schoolId: ctx.schoolId });
  const courses = useCourses({ classId: ctx.child.class?.id ?? '' });
  const attempts = useQuizAttempts({ studentId: ctx.child.memberId });
  const retakes = useQuizRetakes({ studentId: ctx.child.memberId });
  const [taking, setTaking] = useState<Quiz>();
  const mine = new Set((courses.data ?? []).map((c) => c.id));
  const list = (quizzes.data ?? []).filter((q) => mine.has(q.courseId));
  const attemptOf = (q: Quiz) => (attempts.data ?? []).find((a) => a.quizId === q.id);
  const retake = (q: Quiz) => (retakes.data ?? []).some((r) => r.quizId === q.id);
  // to take: open and not answered, or a retake the teacher allowed
  const toTake = list.filter((q) => (attemptOf(q) ? retake(q) : isQuizOpen(q, today)));
  const done = list.filter((q) => !toTake.includes(q));
  const scored = done.flatMap((q) => attemptOf(q) ?? []);
  const average = scored.length
    ? Math.round((scored.reduce((n, a) => n + a.score / a.maxScore, 0) / scored.length) * 100)
    : null;

  return (
    <div className="flex flex-col gap-4">
      <PageHead
        title={t('quizzes.title')}
        note={toTake.length > 0 ? t('quizzes.waiting', { count: toTake.length }) : undefined}
        sub={average !== null ? t('quizzes.average', { value: average }) : undefined}
      />
      {quizzes.isPending ? (
        <Block>
          <div
            className="h-[240px] animate-pulse rounded-[22px]"
            style={{ backgroundColor: PAPER }}
            aria-busy
          />
        </Block>
      ) : list.length === 0 ? (
        <Block>
          <div className="flex flex-col items-center py-14 text-center">
            <TaskArt kind="tests" tone="lavender" size={96} />
            <p className="mt-3 font-display text-[26px] font-light" style={{ color: INK }}>
              {t('quizzes.empty')}
            </p>
          </div>
        </Block>
      ) : (
        <Block>
          {toTake.length > 0 && (
            <section className="mb-7">
              <Head title={t('quizzes.toTake')} count={toTake.length} />
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 2xl:grid-cols-3">
                {toTake.map((q) => (
                  <ToTake
                    key={q.id}
                    quiz={q}
                    attempt={attemptOf(q)}
                    self={ctx.kind === 'student'}
                    onTake={() => setTaking(q)}
                  />
                ))}
              </div>
            </section>
          )}
          {done.length > 0 && (
            <section>
              <Head title={t('quizzes.results')} count={done.length} />
              <ul className="flex flex-col" style={{ borderBottom: `1px solid ${LINE}` }}>
                {done.map((q) => (
                  <ResultRow key={q.id} quiz={q} attempt={attemptOf(q)} />
                ))}
              </ul>
            </section>
          )}
        </Block>
      )}
      {taking && <TakeQuiz quiz={taking} onClose={() => setTaking(undefined)} />}
    </div>
  );
}

const emptyQuestion = (): QuizDraftQuestion => ({
  text: '',
  multiple: false,
  options: ['', ''],
  correct: [],
});

const inputClass =
  'w-full rounded-[14px] bg-white px-3.5 text-[15px] text-[var(--sh-ink)] ring-1 ring-[#E4E0D7] outline-none ring-inset placeholder:text-[rgb(31_26_53/0.42)] focus:ring-2 focus:ring-[var(--sh-dark)]';

function NewQuiz({ courseId, onClose }: { courseId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const actions = useQuizActions();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueOn, setDueOn] = useState(addDays(todayIso(), 3));
  const [showAnswers, setShowAnswers] = useState(true);
  const [questions, setQuestions] = useState<QuizDraftQuestion[]>([emptyQuestion()]);
  const [error, setError] = useState<string>();
  const update = (i: number, q: QuizDraftQuestion) =>
    setQuestions(questions.map((x, j) => (j === i ? q : x)));
  const ready =
    title.trim() &&
    questions.every(
      (q) => q.text.trim() && q.options.filter((o) => o.trim()).length >= 2 && q.correct.length > 0,
    );

  return (
    <Modal title={t('quizzes.new')} onClose={onClose}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          setError(undefined);
          actions.create
            .mutateAsync({
              courseId,
              title,
              description,
              dueOn,
              showAnswers,
              // empty options go, the right ones keep their places
              questions: questions.map((q) => {
                const kept = q.options.map((o, i) => [o.trim(), i] as const).filter(([o]) => o);
                return {
                  ...q,
                  options: kept.map(([o]) => o),
                  correct: kept.flatMap(([, i], n) => (q.correct.includes(i) ? [n] : [])),
                };
              }),
            })
            .then(onClose, () => setError(t('common.error')));
        }}
      >
        <Field
          id="quiz-title"
          label={t('quizzes.titleLabel')}
          required
          maxLength={200}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <Field
          id="quiz-description"
          label={t('quizzes.description')}
          maxLength={2000}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <div className="flex flex-wrap items-end gap-4">
          <Field
            id="quiz-due"
            type="date"
            label={t('quizzes.dueOn')}
            min={todayIso()}
            value={dueOn}
            onChange={(e) => setDueOn(e.target.value)}
            className="w-44"
          />
          <label className="flex items-center gap-2 pb-3 text-[14px]" style={{ color: INK }}>
            <input
              type="checkbox"
              checked={showAnswers}
              onChange={(e) => setShowAnswers(e.target.checked)}
              className="size-4 accent-[var(--sh-dark)]"
            />
            {t('quizzes.showAnswers')}
          </label>
        </div>
        {questions.map((q, i) => (
          // questions have no id before publishing; the list only grows at the end
          <fieldset
            key={i}
            className="flex flex-col gap-2 rounded-[20px] p-3.5"
            style={{ backgroundColor: PAPER }}
          >
            <legend className="float-left mb-1 text-[13px] font-medium" style={{ color: INK_SOFT }}>
              {t('quizzes.question', { n: i + 1 })}
            </legend>
            <input
              aria-label={t('quizzes.questionText')}
              placeholder={t('quizzes.questionText')}
              value={q.text}
              maxLength={1000}
              onChange={(e) => update(i, { ...q, text: e.target.value })}
              className={`${inputClass} h-11`}
            />
            {q.options.map((o, n) => (
              <div key={n} className="flex items-center gap-2.5">
                <input
                  type={q.multiple ? 'checkbox' : 'radio'}
                  name={`correct-${i}`}
                  aria-label={t('quizzes.correctHint')}
                  checked={q.correct.includes(n)}
                  onChange={(e) =>
                    update(i, {
                      ...q,
                      correct: q.multiple
                        ? e.target.checked
                          ? [...q.correct, n]
                          : q.correct.filter((x) => x !== n)
                        : [n],
                    })
                  }
                  className="size-4 shrink-0 accent-[var(--sh-dark)]"
                />
                <input
                  aria-label={t('quizzes.option', { n: n + 1 })}
                  placeholder={t('quizzes.option', { n: n + 1 })}
                  value={o}
                  maxLength={200}
                  onChange={(e) =>
                    update(i, {
                      ...q,
                      options: q.options.map((x, k) => (k === n ? e.target.value : x)),
                    })
                  }
                  className={`${inputClass} h-10 min-w-0 flex-1`}
                />
              </div>
            ))}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pt-1 text-[13px]">
              {q.options.length < 8 && (
                <button
                  type="button"
                  className={`${pillClass('light', 'sm')} pr-3.5`}
                  onClick={() => update(i, { ...q, options: [...q.options, ''] })}
                >
                  {t('quizzes.addOption')}
                </button>
              )}
              <label className="flex items-center gap-1.5" style={{ color: INK }}>
                <input
                  type="checkbox"
                  checked={q.multiple}
                  onChange={(e) =>
                    update(i, {
                      ...q,
                      multiple: e.target.checked,
                      correct: q.correct.slice(0, e.target.checked ? 8 : 1),
                    })
                  }
                  className="size-4 accent-[var(--sh-dark)]"
                />
                {t('quizzes.multipleHint')}
              </label>
              <span style={{ color: INK_FAINT }}>{t('quizzes.correctHint')}</span>
            </div>
          </fieldset>
        ))}
        {questions.length < 50 && (
          <button
            type="button"
            className={`${pillClass('light', 'sm')} self-start pr-3.5`}
            onClick={() => setQuestions([...questions, emptyQuestion()])}
          >
            {t('quizzes.addQuestion')}
          </button>
        )}
        <ErrorText>{error}</ErrorText>
        <Button type="submit" disabled={!ready || actions.create.isPending}>
          {t('quizzes.publish')}
        </Button>
      </form>
    </Modal>
  );
}

function Results({ quiz, onBack }: { quiz: Quiz; onBack: () => void }) {
  const { t } = useTranslation();
  const attempts = useQuizAttempts({ quizId: quiz.id });
  const questions = useQuizQuestions(quiz.id);
  const keys = useQuizKeys(quiz.id);
  const size = useCourseSize(quiz.courseId);
  const actions = useQuizActions();
  const retakes = useQuizRetakes({ quizId: quiz.id });
  const list: QuizAttempt[] = [...(attempts.data ?? [])].sort((a, b) =>
    (a.studentName ?? '').localeCompare(b.studentName ?? '', 'uk'),
  );
  const average = list.length
    ? Math.round((list.reduce((n, a) => n + a.score / a.maxScore, 0) / list.length) * 100)
    : null;
  const look = subjectLook(quiz.subject);
  const tone = SUBJECT_GRAD[look.tone];
  return (
    <div className="flex flex-col gap-4">
      <TitleRow
        title={quiz.title}
        sub={`${quiz.subject} · ${quiz.classLabel} · ${t('quizzes.questions', { count: quiz.questions })} · ${
          isQuizOpen(quiz, todayIso())
            ? t('quizzes.due', { date: shortDate(quiz.dueOn) })
            : t('quizzes.closed')
        }`}
      >
        <button type="button" onClick={onBack} className={`${pillClass('light')} pr-4.5`}>
          {t('quizzes.back')}
        </button>
        <button
          type="button"
          onClick={() => actions.remove.mutate(quiz.id, { onSuccess: onBack })}
          className={`${pillClass('light')} pr-4.5`}
        >
          {t('quizzes.remove')}
        </button>
      </TitleRow>
      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
        <div className="flex min-w-0 flex-col gap-4">
          {/* how the class did: the average big on the subject's colour */}
          <section
            className="relative isolate flex min-w-0 flex-col overflow-hidden rounded-[28px] p-5 md:p-6"
            style={{ backgroundImage: grad(tone), color: GRAD[tone].ink }}
          >
            <SubjectArt
              icon={look.icon}
              tone={tone}
              size={170}
              ghost
              className="pointer-events-none absolute -top-6 -right-6 -z-10"
            />
            <p className="text-[14px] font-medium">{t('quizzes.averageLabel')}</p>
            <p
              className="font-display text-[80px] leading-[88px] font-light tracking-[-0.05em]"
              style={{ color: INK }}
            >
              {average !== null ? `${average}%` : '—'}
            </p>
            <p className="mt-1 text-[14px]" style={{ color: INK_SOFT }}>
              {t('quizzes.answered', { answered: list.length, students: size.data ?? '…' })}
            </p>
          </section>
          <Block>
            <Head title={t('quizzes.byQuestion')} />
            <ol className="flex flex-col gap-4">
              {(questions.data ?? []).map((q) => {
                const right = list.filter((a) =>
                  sameAnswer(a.answers[q.id] ?? [], keys.data?.[q.id] ?? []),
                ).length;
                const share = list.length ? Math.round((right / list.length) * 100) : 0;
                const bar = toneOfShare(share, 100);
                return (
                  <li key={q.id}>
                    <div className="flex justify-between gap-3 text-[14px] leading-5">
                      <span style={{ color: INK }}>
                        <span style={{ color: INK_FAINT }}>{q.position}.</span> {q.text}
                      </span>
                      <span className="shrink-0 tabular-nums" style={{ color: INK_SOFT }}>
                        {t('quizzes.rightShare', { value: share })}
                      </span>
                    </div>
                    <div
                      className="mt-1.5 h-2 overflow-hidden rounded-full"
                      style={{ backgroundColor: PAPER }}
                    >
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${share}%`,
                          backgroundImage: `linear-gradient(90deg, ${GRAD[bar].to}, ${GRAD[bar].ink})`,
                        }}
                      />
                    </div>
                  </li>
                );
              })}
            </ol>
          </Block>
        </div>
        <Block>
          <Head title={t('quizzes.students')} count={list.length} />
          {list.length === 0 ? (
            <p className="text-[15px]" style={{ color: INK_SOFT }}>
              {t('quizzes.answered', { answered: 0, students: size.data ?? '…' })}
            </p>
          ) : (
            <ul className="flex flex-col" style={{ borderBottom: `1px solid ${LINE}` }}>
              {list.map((a) => (
                <li
                  key={a.id}
                  className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 py-3"
                  style={{ borderTop: `1px solid ${LINE}` }}
                >
                  <span className="min-w-0 flex-1">
                    <span
                      className="block text-[15px] leading-[22px] font-medium"
                      style={{ color: INK }}
                    >
                      {a.studentName}
                    </span>
                    {a.awayCount > 0 && (
                      <span
                        className="flex items-center gap-1 text-[12.5px] leading-[18px]"
                        style={{ color: GRAD.coral.ink }}
                      >
                        <WarningCircleIcon size={14} weight="fill" aria-hidden />
                        {t('quizzes.away', { count: a.awayCount, time: awayTime(a.awaySeconds) })}
                      </span>
                    )}
                    {a.attemptNo > 1 && a.firstScore !== null && (
                      <span
                        className="block text-[12.5px] leading-[18px]"
                        style={{ color: INK_SOFT }}
                      >
                        {t('quizzes.attemptNo', { n: a.attemptNo })} ·{' '}
                        {t('quizzes.firstScore', { score: a.firstScore, max: a.maxScore })}
                      </span>
                    )}
                  </span>
                  <span className="flex items-center gap-2">
                    {(retakes.data ?? []).some((r) => r.studentId === a.studentId) ? (
                      <Mark tone="lavender">{t('quizzes.retakeOpen')}</Mark>
                    ) : (
                      <button
                        type="button"
                        onClick={() =>
                          actions.allowRetake.mutate({ quizId: quiz.id, studentId: a.studentId })
                        }
                        className="inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-[12.5px] font-medium transition-colors hover:bg-[#F5F3EF] hover:text-[var(--sh-ink)]"
                        style={{ color: INK_SOFT }}
                      >
                        <ArrowCounterClockwiseIcon size={13} weight="bold" aria-hidden />
                        {t('quizzes.allowRetake')}
                      </button>
                    )}
                    <Score attempt={a} />
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Block>
      </div>
    </div>
  );
}

function StaffView({ ctx }: { ctx: Extract<ViewContext, { kind: 'teacher' | 'admin' }> }) {
  const { t } = useTranslation();
  const today = todayIso();
  const classes = useClasses(ctx.schoolId);
  const [classId, setClassId] = useState('');
  const klass = classId || classes.data?.[0]?.id || '';
  const courses = useCourses(
    ctx.kind === 'teacher' ? { teacherMemberId: ctx.memberId } : { classId: klass },
  );
  const [courseId, setCourseId] = useState('');
  const course = courseId || courses.data?.[0]?.id || '';
  const quizzes = useQuizzes({ courseId: course });
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState<string>();
  const open = (quizzes.data ?? []).find((q) => q.id === openId);
  const subject = (courses.data ?? []).find((c) => c.id === course)?.subject ?? '';
  const look = subjectLook(subject);

  if (open) return <Results quiz={open} onBack={() => setOpenId(undefined)} />;
  return (
    <div className="flex flex-col gap-4">
      <TitleRow title={t('quizzes.title')}>
        {ctx.kind === 'admin' && (
          <select
            aria-label={t('quizzes.course')}
            className={selectClass}
            value={klass}
            onChange={(e) => {
              setClassId(e.target.value);
              setCourseId('');
            }}
          >
            {(classes.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        )}
        <select
          aria-label={t('quizzes.course')}
          className={selectClass}
          value={course}
          onChange={(e) => setCourseId(e.target.value)}
        >
          {(courses.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.subject}
              {c.groupLabel ? ` (${c.groupLabel})` : ''} · {c.classLabel}
            </option>
          ))}
        </select>
        {course && (
          <button type="button" onClick={() => setCreating(true)} className={pillClass('dark')}>
            {t('quizzes.new')}
            <PillIcon icon={PlusIcon} />
          </button>
        )}
      </TitleRow>
      <Block>
        {quizzes.isPending ? (
          <Spinner />
        ) : (quizzes.data ?? []).length === 0 ? (
          <div className="flex flex-col items-center py-12 text-center">
            <TaskArt kind="tests" tone="lavender" size={88} />
            <p className="mt-3 font-display text-[24px] font-light" style={{ color: INK }}>
              {t('quizzes.empty')}
            </p>
          </div>
        ) : (
          <ul className="flex flex-col">
            {(quizzes.data ?? []).map((q, i) => {
              const live = isQuizOpen(q, today);
              return (
                <li key={q.id} style={i > 0 ? { borderTop: `1px solid ${LINE}` } : undefined}>
                  <button
                    type="button"
                    onClick={() => setOpenId(q.id)}
                    className="group flex w-full items-center gap-4 py-3 text-left"
                  >
                    <SubjectArt icon={look.icon} tone={SUBJECT_GRAD[look.tone]} size={40} />
                    <span className="min-w-0 flex-1">
                      <span
                        className="block text-[16px] leading-[22px] font-medium"
                        style={{ color: INK }}
                      >
                        {q.title}
                      </span>
                      <span className="block text-[12.5px]" style={{ color: INK_SOFT }}>
                        {t('quizzes.questions', { count: q.questions })}
                        {/* on a phone the state stays in the line: the mark would squeeze the name */}
                        <span className="sm:hidden">
                          {' · '}
                          {live
                            ? t('quizzes.due', { date: shortDate(q.dueOn) })
                            : t('quizzes.closed')}
                        </span>
                      </span>
                    </span>
                    <span className="hidden sm:inline-flex">
                      <Mark tone={live ? 'cobalt' : 'stone'}>
                        {live
                          ? t('quizzes.due', { date: shortDate(q.dueOn) })
                          : t('quizzes.closed')}
                      </Mark>
                    </span>
                    <span
                      className={`${ringSmall} group-hover:bg-[var(--sh-dark)] group-hover:text-white`}
                    >
                      <ArrowUpRightIcon size={14} weight="bold" aria-hidden />
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </Block>
      {creating && course && <NewQuiz courseId={course} onClose={() => setCreating(false)} />}
    </div>
  );
}

export function QuizzesPage() {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  return ctx.kind === 'student' || ctx.kind === 'guardian' ? (
    <FamilyView ctx={ctx} />
  ) : ctx.kind === 'psychologist' ? (
    <PageHead title={t('quizzes.title')} />
  ) : (
    <StaffView ctx={ctx} />
  );
}
